import { ReactNode } from 'react';

interface PageLayoutProps {
  children: ReactNode;
}

/**
 * Page Layout Component
 *
 * Compositor component for page layouts with consistent spacing and padding.
 * Stacks children vertically with automatic gap handling using Flexbox.
 *
 * @param {PageLayoutProps} props - Component properties
 * @param {ReactNode} props.children - Child components in render order
 * @returns {React.ReactElement} Page layout component
 *
 * @example
 * <PageLayout>
 *   <PrimaryHeader title="Personal" description="..." />
 *   <PersonnelTabs activeTab="roles" onTabChange={...} />
 *   <SecondaryHeader title="Roles" description="..." />
 *   <RolesSearchBar />
 *   <RolesTable />
 * </PageLayout>
 *
 * @example
 * // With breadcrumbs (form pages)
 * <PageLayout>
 *   <Breadcrumbs items={[...]} />
 *   <SecondaryHeader title="Create Role" description="..." actionButton={...} />
 *   <RoleForm onSubmit={...} />
 * </PageLayout>
 */
export const PageLayout = ({ children }: PageLayoutProps): React.ReactElement => {
  return (
    <div className="flex w-full min-w-0 flex-col gap-4 px-4 py-4 font-sans max-[599px]:mt-14 md:px-6">{children}</div>
  );
};
